import React, { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { getContent, adminUpdateContent } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const ICON_OPTIONS = ["Home", "Layers", "Building2", "Hammer", "Ruler", "Bath", "Warehouse", "Wrench", "Lightbulb", "ShieldCheck"];
const SUBTABS = [
  { key: "contact", label: "Contact Details" },
  { key: "services", label: "Services" },
  { key: "faqs", label: "FAQs" },
  { key: "blog", label: "Blog Posts" },
  { key: "projects", label: "Projects" },
];

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-homi-gold";
const labelCls = "block text-xs font-heading font-semibold text-gray-500 mb-1";

export default function ContentPanel() {
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [sub, setSub] = useState("contact");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getContent().then(setContent).catch(() => toast({ title: "Could not load content" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdateContent(content);
      setContent(updated);
      toast({ title: "Saved", description: "Your changes are now live on the website." });
    } catch {
      toast({ title: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-homi-gold" /></div>;

  const setList = (key, list) => setContent((c) => ({ ...c, [key]: list }));
  const updateItem = (key, i, patch) => setList(key, content[key].map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (key, i) => setList(key, content[key].filter((_, idx) => idx !== i));
  const addItem = (key, item) => setList(key, [...(content[key] || []), item]);

  const contact = content.contact || {};
  const setContact = (patch) => setContent((c) => ({ ...c, contact: { ...c.contact, ...patch } }));

  return (
    <div data-testid="content-panel">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {SUBTABS.map((t) => (
            <button key={t.key} data-testid={`content-subtab-${t.key}`} onClick={() => setSub(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-heading font-semibold transition-colors ${sub === t.key ? "bg-homi-black text-white" : "bg-white text-homi-black border border-gray-200 hover:bg-gray-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <button data-testid="content-save" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-homi-gold hover:bg-homi-goldDark text-homi-black font-heading font-bold text-sm px-5 py-2.5 rounded-lg transition-colors disabled:opacity-70">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
        </button>
      </div>

      {sub === "contact" && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm max-w-xl space-y-4" data-testid="editor-contact">
          <div><label className={labelCls}>Phone (displayed)</label><input className={inputCls} value={contact.phone || ""} onChange={(e) => setContact({ phone: e.target.value })} /></div>
          <div><label className={labelCls}>WhatsApp number (digits only, e.g. 447872164350)</label><input className={inputCls} value={contact.whatsapp || ""} onChange={(e) => setContact({ whatsapp: e.target.value })} /></div>
          <div><label className={labelCls}>Email</label><input className={inputCls} value={contact.email || ""} onChange={(e) => setContact({ email: e.target.value })} /></div>
          <div><label className={labelCls}>Address</label><input className={inputCls} value={contact.address || ""} onChange={(e) => setContact({ address: e.target.value })} /></div>
          <div><label className={labelCls}>Opening hours</label><input className={inputCls} value={contact.hours || ""} onChange={(e) => setContact({ hours: e.target.value })} /></div>
        </div>
      )}

      {sub === "services" && (
        <div className="space-y-4" data-testid="editor-services">
          {(content.services || []).map((s, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Title</label><input className={inputCls} value={s.title || ""} onChange={(e) => updateItem("services", i, { title: e.target.value })} /></div>
                <div><label className={labelCls}>Icon</label>
                  <select className={inputCls} value={s.icon || "Wrench"} onChange={(e) => updateItem("services", i, { icon: e.target.value })}>
                    {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3"><label className={labelCls}>Description</label><textarea rows={2} className={inputCls} value={s.desc || ""} onChange={(e) => updateItem("services", i, { desc: e.target.value })} /></div>
              <button onClick={() => removeItem("services", i)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /> Remove</button>
            </div>
          ))}
          <button onClick={() => addItem("services", { icon: "Wrench", title: "New Service", desc: "" })} className="inline-flex items-center gap-2 text-sm font-heading font-bold text-homi-goldDark"><Plus className="w-4 h-4" /> Add service</button>
        </div>
      )}

      {sub === "faqs" && (
        <div className="space-y-4" data-testid="editor-faqs">
          {(content.faqs || []).map((f, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div><label className={labelCls}>Question</label><input className={inputCls} value={f.q || ""} onChange={(e) => updateItem("faqs", i, { q: e.target.value })} /></div>
              <div className="mt-3"><label className={labelCls}>Answer</label><textarea rows={2} className={inputCls} value={f.a || ""} onChange={(e) => updateItem("faqs", i, { a: e.target.value })} /></div>
              <button onClick={() => removeItem("faqs", i)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /> Remove</button>
            </div>
          ))}
          <button onClick={() => addItem("faqs", { q: "New question?", a: "" })} className="inline-flex items-center gap-2 text-sm font-heading font-bold text-homi-goldDark"><Plus className="w-4 h-4" /> Add FAQ</button>
        </div>
      )}

      {sub === "blog" && (
        <div className="space-y-4" data-testid="editor-blog">
          {(content.blog || []).map((b, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Title</label><input className={inputCls} value={b.title || ""} onChange={(e) => updateItem("blog", i, { title: e.target.value })} /></div>
                <div><label className={labelCls}>Slug (URL)</label><input className={inputCls} value={b.slug || ""} onChange={(e) => updateItem("blog", i, { slug: e.target.value })} /></div>
                <div><label className={labelCls}>Category</label><input className={inputCls} value={b.category || ""} onChange={(e) => updateItem("blog", i, { category: e.target.value })} /></div>
                <div><label className={labelCls}>Author</label><input className={inputCls} value={b.author || ""} onChange={(e) => updateItem("blog", i, { author: e.target.value })} /></div>
                <div><label className={labelCls}>Date</label><input className={inputCls} value={b.date || ""} onChange={(e) => updateItem("blog", i, { date: e.target.value })} /></div>
                <div><label className={labelCls}>Image URL</label><input className={inputCls} value={b.image || ""} onChange={(e) => updateItem("blog", i, { image: e.target.value })} /></div>
              </div>
              <div className="mt-3"><label className={labelCls}>Excerpt</label><textarea rows={2} className={inputCls} value={b.excerpt || ""} onChange={(e) => updateItem("blog", i, { excerpt: e.target.value })} /></div>
              <div className="mt-3"><label className={labelCls}>Body (separate paragraphs with a blank line)</label>
                <textarea rows={5} className={inputCls} value={(b.body || []).join("\n\n")} onChange={(e) => updateItem("blog", i, { body: e.target.value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) })} />
              </div>
              <button onClick={() => removeItem("blog", i)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /> Remove</button>
            </div>
          ))}
          <button onClick={() => addItem("blog", { slug: `new-post-${Date.now()}`, title: "New Post", excerpt: "", category: "Tips", author: "EMB", date: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }), image: "", body: [] })} className="inline-flex items-center gap-2 text-sm font-heading font-bold text-homi-goldDark"><Plus className="w-4 h-4" /> Add blog post</button>
        </div>
      )}

      {sub === "projects" && (
        <div className="space-y-4" data-testid="editor-projects">
          {(content.projects || []).map((p, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Title</label><input className={inputCls} value={p.title || ""} onChange={(e) => updateItem("projects", i, { title: e.target.value })} /></div>
                <div><label className={labelCls}>Category</label><input className={inputCls} value={p.category || ""} onChange={(e) => updateItem("projects", i, { category: e.target.value })} /></div>
                <div><label className={labelCls}>Before image URL</label><input className={inputCls} value={p.before || ""} onChange={(e) => updateItem("projects", i, { before: e.target.value })} /></div>
                <div><label className={labelCls}>After image URL</label><input className={inputCls} value={p.image || ""} onChange={(e) => updateItem("projects", i, { image: e.target.value })} /></div>
              </div>
              <button onClick={() => removeItem("projects", i)} className="mt-3 inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /> Remove</button>
            </div>
          ))}
          <button onClick={() => addItem("projects", { id: Date.now(), title: "New Project", category: (content.categories || []).find((c) => c !== "All") || "Kitchen", before: "", image: "" })} className="inline-flex items-center gap-2 text-sm font-heading font-bold text-homi-goldDark"><Plus className="w-4 h-4" /> Add project</button>
        </div>
      )}
    </div>
  );
}
